import type {
  DailySheetResource,
  DailySheetRowResource,
  ParkExpenseResource,
} from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconPlus } from '@tabler/icons-react';
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

import { formatPaise } from '@/lib/money';

import { ScreenTable } from '../back-office/Screen';
import { paths } from '../paths';
import { formatRate } from '../revenue-shares/rate';
import { formatBusinessDate } from '../tickets/business-date';

/**
 * One page of the park's book (#276): the opening balance, the outlets in the
 * owner's blocks each closed by a sub total, any outlet the layout does not
 * place, the park's own expenses, then Total, Deductions and Closing.
 *
 * Every figure is the API's. This computes nothing — not a sub total, not a
 * deduction — so it can be checked against a photographed page line for line.
 * Three figures are traps for a screen that tries to be helpful:
 *
 * - **`upiPaise: null` is "not declared", never ₹0.** Nobody entered that
 *   outlet's GPay, so nothing came off; zero would say none was taken.
 * - **`partnerPaymentsPaise` is disclosed, not subtracted.** The share already
 *   came off as it accrued; the payment is the same money leaving.
 * - **An unplaced outlet's money is already in every total.** The layout says
 *   where a row is drawn, never what is added up.
 *
 * Partners are columns named from each row's own `shares[].vendor.name` — the
 * set is vendor records with agreements and changes without a deploy, so no
 * list of them is written down here.
 */

interface Partner {
  readonly id: string;
  readonly name: string;
  /** Every rate in force on the page, first seen first — two when it differs by outlet. */
  readonly rates: readonly number[];
}

/** One column per partner with a share anywhere on the page, first seen first. */
function partnersIn(rows: readonly DailySheetRowResource[]): readonly Partner[] {
  const byId = new Map<string, { name: string; rates: number[] }>();

  for (const row of rows) {
    for (const share of row.shares) {
      const held = byId.get(share.vendor.id) ?? { name: share.vendor.name, rates: [] };

      if (!held.rates.includes(share.rateMilliPercent)) held.rates.push(share.rateMilliPercent);
      byId.set(share.vendor.id, held);
    }
  }

  return [...byId].map(([id, { name, rates }]) => ({ id, name, rates }));
}

/** Outlet, collection, GPay, expenses — the partners sit between the last two. */
const FIXED_COLUMNS = 4;

const amountCell = 'text-right font-mono numeric';

export function SheetDay({
  day,
  onAddExpense,
  onCorrectExpense,
}: {
  day: DailySheetResource;
  onAddExpense: (businessDate: string) => void;
  onCorrectExpense: (line: ParkExpenseResource) => void;
}) {
  const date = formatBusinessDate(day.businessDate);
  const rows = [...day.groups.flatMap((group) => group.rows), ...day.unplaced];
  const partners = partnersIn(rows);
  const columns = FIXED_COLUMNS + partners.length;

  /** A line whose figure sits in the collection column — the column the book sums. */
  const collectionLine = (label: string, paise: number, strong = false) => (
    <TableRow className={strong ? 'font-medium hover:bg-transparent' : 'hover:bg-transparent'}>
      <TableCell>{label}</TableCell>
      <TableCell className={amountCell}>{formatPaise(paise)}</TableCell>
      <TableCell colSpan={columns - 2} />
    </TableRow>
  );

  /** A heading across the page, for a block the book draws apart. */
  const heading = (children: ReactNode) => (
    <TableRow className="hover:bg-transparent">
      <TableCell
        colSpan={columns}
        className="pt-6 font-display text-xs font-semibold tracking-[0.16em] whitespace-normal text-muted-foreground uppercase"
      >
        {children}
      </TableCell>
    </TableRow>
  );

  return (
    <section aria-label={date} className="space-y-3">
      <h2 className="font-medium text-foreground numeric">{date}</h2>

      <ScreenTable label={`Daily sheet, ${date}`}>
        <TableHeader>
          <TableRow>
            <TableHead>Outlet</TableHead>
            <TableHead className="text-right">Collection</TableHead>
            <TableHead className="text-right">GPay</TableHead>
            {partners.map((partner) => (
              <TableHead key={partner.id} className="text-right">
                {partner.name}
                <br />
                <span className="text-xs text-muted-foreground numeric">
                  {partner.rates.map(formatRate).join(' / ')}
                </span>
              </TableHead>
            ))}
            <TableHead className="text-right">Expenses</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {collectionLine('Opening balance', day.openingBalancePaise, true)}

          {day.groups.map((group, index) => (
            <Fragment key={index}>
              {group.rows.map((row) => (
                <OutletRow key={row.outlet.id} row={row} partners={partners} />
              ))}
              {collectionLine('Sub total', group.collectionPaise, true)}
            </Fragment>
          ))}

          {day.unplaced.length > 0 && (
            <>
              {heading(
                <>
                  Not placed in the layout{' '}
                  <span className="font-sans font-normal tracking-normal normal-case">
                    — already in every total.{' '}
                    <Link
                      to={paths.dailySheetLayout}
                      className="text-foreground underline underline-offset-4"
                    >
                      Place them in the layout
                    </Link>
                  </span>
                </>,
              )}
              {day.unplaced.map((row) => (
                <OutletRow key={row.outlet.id} row={row} partners={partners} />
              ))}
            </>
          )}

          {heading('Park expenses')}

          {day.parkExpenses.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns} className="text-muted-foreground">
                None recorded for this day.
              </TableCell>
            </TableRow>
          ) : (
            day.parkExpenses.map((line) => (
              <TableRow key={line.id}>
                <TableCell>
                  <span className="me-3">{line.reason}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Correct ${line.reason}`}
                    onClick={() => onCorrectExpense(line)}
                  >
                    Correct
                  </Button>
                </TableCell>
                <TableCell colSpan={columns - 2} />
                <TableCell className={amountCell}>{formatPaise(line.amountPaise)}</TableCell>
              </TableRow>
            ))
          )}

          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns}>
              <Button variant="outline" size="sm" onClick={() => onAddExpense(day.businessDate)}>
                <IconPlus data-icon="inline-start" />
                Add a park expense
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </ScreenTable>

      <ScreenTable label={`Day totals, ${date}`}>
        <TableBody>
          <TotalLine label="Total" detail="opening + collections" paise={day.totalPaise} strong />
          <TotalLine label="GPay" paise={day.upiPaise} />
          <TotalLine label="Partners’ shares" paise={day.sharesPaise} />
          <TotalLine label="Expenses" detail="outlets and park" paise={day.expensesPaise} />
          <TotalLine label="Deductions" paise={day.deductionsPaise} strong />
          <TotalLine label="Closing balance" paise={day.closingBalancePaise} strong />
          {day.partnerPaymentsPaise !== 0 && (
            <TotalLine
              label="Paid to partners"
              detail="not subtracted"
              paise={day.partnerPaymentsPaise}
            />
          )}
        </TableBody>
      </ScreenTable>
    </section>
  );
}

function OutletRow({
  row,
  partners,
}: {
  row: DailySheetRowResource;
  partners: readonly Partner[];
}) {
  return (
    <TableRow>
      <TableCell>
        <span className="me-2 font-mono text-xs text-muted-foreground numeric">
          {row.outlet.code}
        </span>
        {row.outlet.name}
      </TableCell>
      <TableCell className={amountCell}>{formatPaise(row.collectionPaise)}</TableCell>
      <TableCell className={amountCell}>
        {/* Never `?? 0`: see the note at the head of this file. */}
        {row.upiPaise === null ? (
          <span className="font-sans text-muted-foreground">Not declared</span>
        ) : (
          formatPaise(row.upiPaise)
        )}
      </TableCell>
      {partners.map((partner) => {
        const share = row.shares.find((candidate) => candidate.vendor.id === partner.id);

        return (
          <TableCell key={partner.id} className={amountCell}>
            {share === undefined ? (
              <>
                <span aria-hidden>—</span>
                <span className="sr-only">No agreement at this outlet</span>
              </>
            ) : (
              <>
                {formatPaise(share.sharePaise)}
                {partner.rates.length > 1 && (
                  <span className="block text-xs text-muted-foreground">
                    at {formatRate(share.rateMilliPercent)}
                  </span>
                )}
              </>
            )}
          </TableCell>
        );
      })}
      <TableCell className={amountCell}>
        {formatPaise(row.expensesPaise)}
        {/* A disclosure, not a term: it is in no figure on this page. */}
        {row.partnerPaymentsPaise !== 0 && (
          <span className="block font-sans text-xs text-muted-foreground">
            <span className="font-mono numeric">{formatPaise(row.partnerPaymentsPaise)}</span> paid
            to partner, not subtracted
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

function TotalLine({
  label,
  detail,
  paise,
  strong = false,
}: {
  label: string;
  detail?: string;
  paise: number;
  strong?: boolean;
}) {
  return (
    <TableRow className={strong ? 'font-medium hover:bg-transparent' : 'hover:bg-transparent'}>
      <TableCell>
        {label}
        {detail !== undefined && (
          <span className="ms-2 text-sm font-normal text-muted-foreground">{detail}</span>
        )}
      </TableCell>
      <TableCell className={amountCell}>{formatPaise(paise)}</TableCell>
    </TableRow>
  );
}
